import { useEffect, useRef, useState } from 'react'
import api from '../../api/axios'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
  BellAlertIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ChatBubbleLeftEllipsisIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline'
import DeviceMessage from './DeviceMessage'
import DeviceTestPush from './DeviceTestPush'
import Fingerprint from './Fingerprint'
import {
  type Device,
  type HistoryEntry,
  type NativeBuild,
  type OtaRelease,
  STATUS_STYLE,
  buildLabel,
  buildsForRuntime,
  channelOf,
  daysSince,
  deviceName,
  formatWhen,
  otaLabel,
  platformLabel,
  shortFp,
  storeLabel,
  timeAgo,
  updateName,
  versionLine,
} from './fleet'

/** What a history entry ran on top of the store build. */
function historyOta(h: HistoryEntry) {
  const embedded = h.is_embedded === true || h.is_embedded === 1
  if (embedded || h.ota_build === 0) return 'built-in bundle'
  if (h.is_embedded === false || h.is_embedded === 0) return h.ota_build || h.ota_version ? updateName(h) : 'downloaded update'
  if (h.ota_build == null) return 'reported without update info'
  return `${updateName(h)} (as reported)`
}

function errMsg(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback
}

export default function DeviceDrawer({
  device,
  release,
  latestRelease,
  builds,
  onClose,
  onChanged,
  notify,
}: {
  device: Device
  /** The recorded release the device runs, if any. */
  release: OtaRelease | null
  /** The newest release for its platform + channel + runtime. */
  latestRelease: OtaRelease | null
  builds: NativeBuild[]
  onClose: () => void
  onChanged: () => void
  notify: (msg: string, tone?: 'ok' | 'error') => void
}) {
  const [history, setHistory] = useState<HistoryEntry[] | null>(null)
  const [installs, setInstalls] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [composing, setComposing] = useState(false)
  const [testing, setTesting] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)
  const status = device.status

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let cancelled = false
    setHistory(null)
    api
      .get(`/super-admin/devices/${device.id}/history`)
      .then((r) => {
        if (cancelled) return
        setHistory(r.data.history || [])
        setInstalls(r.data.installs || [])
      })
      .catch(() => !cancelled && setHistory([]))
    return () => {
      cancelled = true
    }
  }, [device.id])

  const act = async (path: string, done: string, fail: string) => {
    setBusy(true)
    try {
      await api.post(`/super-admin/devices/${device.id}/${path}`)
      notify(done)
      onChanged()
    } catch (err) {
      notify(errMsg(err, fail), 'error')
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    const lines = [
      `${deviceName(device)} (${device.company_name || device.company_code})`,
      `${platformLabel(device.platform)} ${device.os_version ?? ''} ${device.device_model ?? ''}`.trim(),
      versionLine(device),
      `Runtime ${device.runtime_version ?? 'unknown'}`,
      `Status: ${status.label}`,
      `Channel ${channelOf(device)} · update ${device.update_id ?? 'none'}${status.embedded ? ' (built-in bundle)' : ''}`,
      `Device ${device.id} · last check-in ${formatWhen(device.last_seen_at)}`,
    ]
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      notify('Device details copied')
    } catch {
      notify("Couldn't copy — your browser blocked clipboard access", 'error')
    }
  }

  const stale = daysSince(device.last_seen_at) > 14
  const sameBuild = buildsForRuntime(builds, device.platform, device.runtime_version)
  const warning =
    status.key === 'store' || status.key === 'unknown' || status.key === 'unrecorded'
      ? status.detail
      : device.superseded_by
        ? `Merged: this install was replaced by device ${device.superseded_by} (same person, same ${device.device_model || 'model'}, reinstalled). Its history carries over. If it checks in again it is restored on its own.`
        : stale
          ? `Not seen for ${Math.round(daysSince(device.last_seen_at))} days${device.inactive ? ', so it is inactive and left out of counts' : ''}. The version is what it reported last. Retire it if the phone is gone.`
          : null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-drawer-title"
        className="absolute top-0 right-0 bottom-0 w-full max-w-[460px] bg-white shadow-2xl flex flex-col"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex justify-between gap-3">
          <div className="min-w-0">
            <h2 id="device-drawer-title" className="text-lg font-semibold text-slate-900 truncate">{deviceName(device)}</h2>
            <p className="text-sm text-slate-500 truncate">
              {device.employee_id ? `${device.employee_id} · ` : ''}
              {device.company_name || device.company_code}
            </p>
          </div>
          <button ref={closeRef} onClick={onClose} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" aria-label="Close">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {warning && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-slate-800">
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span>{warning}</span>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 p-4 space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Running now</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[status.key]}`}>{status.label}</span>
            </div>
            <div className="font-mono text-lg font-semibold text-slate-900">
              {storeLabel(device)} <span className="text-slate-300">·</span> {otaLabel(device)}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-slate-500">
              <span>Runtime</span>
              <Fingerprint value={device.runtime_version} className="text-slate-700" />
              {sameBuild.length > 0 && <span className="text-xs">({sameBuild.map(buildLabel).join(', ')})</span>}
            </div>
            <p className="text-sm text-slate-600">{status.detail}</p>
            {latestRelease && (!release || latestRelease.id !== release.id) && (
              <div className="text-xs text-slate-500">
                Latest for this build: <span className="font-mono">{updateName(latestRelease)}</span> ({latestRelease.channel}, {formatWhen(latestRelease.published_at)})
              </div>
            )}
            {release?.message && <div className="text-xs text-slate-500">Running: {release.message}</div>}
            {device.update_created_at && (
              <div className="text-xs text-slate-400">
                {status.embedded ? 'Bundle built' : 'Update published'} {formatWhen(device.update_created_at)}
              </div>
            )}
          </div>

          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Device</h3>
            <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-400">Platform</dt>
              <dd className="text-slate-800">{platformLabel(device.platform)}{device.os_version ? ` ${device.os_version}` : ''}</dd>
              <dt className="text-slate-400">Model</dt>
              <dd className="text-slate-800">{device.device_model || (device.platform === 'ios' ? 'Not reported on iOS yet' : 'Not reported')}</dd>
              <dt className="text-slate-400">Channel</dt>
              <dd className="text-slate-800">{channelOf(device)}</dd>
              <dt className="text-slate-400">Update ID</dt>
              <dd className="font-mono text-xs text-slate-700 break-all">
                {device.update_id || '—'}
                {status.embedded === true && <span className="font-sans text-slate-400"> (built-in bundle)</span>}
              </dd>
              <dt className="text-slate-400">Runtime</dt>
              <dd className="font-mono text-xs text-slate-700 break-all">{device.runtime_version || 'not reported'}</dd>
              {device.retired_reason && (
                <>
                  <dt className="text-slate-400">Retired</dt>
                  <dd className="text-slate-800">{device.retired_reason}</dd>
                </>
              )}
              <dt className="text-slate-400">First seen</dt>
              <dd className="text-slate-800">{formatWhen(device.created_at)}</dd>
              <dt className="text-slate-400">Last check-in</dt>
              <dd className={stale ? 'text-amber-700' : 'text-slate-800'}>{formatWhen(device.last_seen_at)} ({timeAgo(device.last_seen_at)})</dd>
            </dl>
          </section>

          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Version history</h3>
            {history === null ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-400">No history yet. It starts with this phone's next check-in on the updated app.</p>
            ) : (
              <ol className="relative border-l-2 border-slate-200 ml-1.5 space-y-3">
                {history.map((h, i) => (
                  <li key={`${h.seen_at}-${i}`} className="pl-4 relative">
                    <span
                      className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 ${i === 0 ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-400'}`}
                    />
                    <div className="flex justify-between gap-3 text-sm">
                      <span>
                        <b className="font-mono">
                          {h.app_version ?? '?'}
                          {h.native_build ? ` (${h.native_build})` : ''}
                        </b>{' '}
                        <span className="text-slate-500">
                          {historyOta(h)}
                          {h.runtime_version ? ` · ${shortFp(h.runtime_version)}` : ''}
                        </span>
                        {h.device_id && h.device_id !== device.id && (
                          <span className="ml-1.5 text-[11px] px-1 rounded bg-slate-100 text-slate-500">earlier install</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-400 whitespace-nowrap">{formatWhen(h.seen_at)}</span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {composing && device.employee_id && (
            <DeviceMessage device={device} notify={notify} onClose={() => setComposing(false)} />
          )}
          {testing && device.employee_id && (
            <DeviceTestPush device={device} notify={notify} onClose={() => setTesting(false)} />
          )}

          <div className="flex flex-wrap gap-2">
            {device.employee_id && !composing && (
              <button
                onClick={() => { setComposing(true); setTesting(false) }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                <ChatBubbleLeftEllipsisIcon className="w-4 h-4" /> Send a message
              </button>
            )}
            {device.employee_id && !testing && (
              <button
                onClick={() => { setTesting(true); setComposing(false) }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                <BeakerIcon className="w-4 h-4" /> Test notification
              </button>
            )}
            <button onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
              <ClipboardDocumentIcon className="w-4 h-4" /> Copy details
            </button>
            {(status.key === 'behind' || status.key === 'embedded' || status.key === 'store') && device.employee_id && (
              <button
                disabled={busy}
                onClick={() => act('remind', `Reminder sent to ${deviceName(device)}`, "Couldn't send the reminder")}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <BellAlertIcon className="w-4 h-4" /> Send update reminder
              </button>
            )}
            {device.retired_at ? (
              <button
                disabled={busy}
                onClick={() => act('restore', 'Device restored', "Couldn't restore the device")}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" /> Restore device
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={() => act('retire', `${device.device_model || 'Device'} retired`, "Couldn't retire the device")}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <ArchiveBoxIcon className="w-4 h-4" /> Retire device
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400">
            Retire hides a phone that's lost, replaced or wiped. If it checks in again it comes back on its own.
            {installs.length > 1 ? ` History includes ${installs.length - 1} earlier install${installs.length > 2 ? 's' : ''} of this phone.` : ''}
          </p>
        </div>
      </aside>
    </div>
  )
}
